<template>
  <v-main>
    <!-- fill-height también en la fila; ver la nota en Welcome.vue. -->
    <v-container fluid class="fill-height">
      <v-row align="center" justify="center" class="fill-height">
        <v-col cols="12" sm="8" md="7" lg="6" xl="5">
          <v-card class="elevation-12">
            <v-row class="pa-3 ma-0">
              <v-col
                v-for="portal in visiblePortals"
                :key="portal.path"
                cols="12"
                xs="12"
                :md="columnWidth"
              >
                <!-- v-hover cambió sus slot props: { hover } -> { isHovering, props } -->
                <v-hover v-slot="{ isHovering, props }">
                  <v-sheet
                    v-bind="props"
                    border
                    :elevation="isHovering ? 12 : 0"
                    height="330"
                    style="cursor: pointer"
                    @click="goTo(portal.path)"
                  >
                    <v-row>
                      <v-col cols="12" class="text-center">
                        <v-icon size="200" :color="portal.color">{{ portal.icon }}</v-icon>
                      </v-col>
                      <v-col cols="12">
                        <div class="text-center px-4">
                          <div class="text-button" :class="`text-${portal.color}-darken-2`">
                            {{ portal.title }}
                          </div>
                          <div class="text-body-2 text-medium-emphasis">
                            {{ portal.subtitle }}
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-sheet>
                </v-hover>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script setup lang="ts">
/**
 * Selector de portal.
 *
 * Antes mostraba las tarjetas de forma fija y sólo distinguía "tiene algún rol
 * ADMIN" de "no tiene ninguno", así que un usuario sin permisos de sistema veía
 * igual la tarjeta de Sistema y aterrizaba en un 403. Acá cada tarjeta aparece
 * únicamente si el usuario puede entrar a alguna sección de ese portal.
 */
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { ADMIN_SECTIONS, MY_SECTIONS, SYSTEM_SECTIONS, firstAccessible } from "@/router/sections";

const router = useRouter();
const auth = useAuthStore();

const can = (roles: Parameters<typeof auth.canAccess>[0]) => auth.canAccess(roles);

const visiblePortals = computed(() => {
  const portals: Array<{
    path: string;
    title: string;
    subtitle: string;
    icon: string;
    color: string;
  }> = [];

  const my = firstAccessible(MY_SECTIONS, can);
  if (my) {
    portals.push({
      path: my,
      title: "Portal Usuario",
      subtitle: "Maneja tu cuenta y certificados digitales",
      icon: "mdi-account-cog-outline",
      color: "green",
    });
  }

  const admin = firstAccessible(ADMIN_SECTIONS, can);
  if (admin) {
    portals.push({
      path: admin,
      title: "Portal Admin",
      subtitle: "Administra usuarios, llaves y certificados",
      icon: "mdi-application-cog",
      color: "indigo",
    });
  }

  const system = firstAccessible(SYSTEM_SECTIONS, can);
  if (system) {
    portals.push({
      path: system,
      title: "Portal Sistema",
      subtitle: "Configura las llaves de sistema",
      icon: "mdi-server-security",
      color: "blue-grey",
    });
  }

  return portals;
});

/** Reparte el ancho según cuántas tarjetas se muestran. */
const columnWidth = computed(() => {
  const n = visiblePortals.value.length;
  return n >= 3 ? 4 : n === 2 ? 6 : 12;
});

function goTo(path: string) {
  router.push({ path });
}
</script>
